import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

/**
 * Landing page for the link in the verification email.
 *
 * The API endpoint returns JSON; this page calls it and shows the result as
 * something a player can read. Without it the emailed link renders raw JSON,
 * which looks broken.
 */

type State =
  | { kind: "working" }
  | { kind: "ok" }
  | { kind: "failed"; reason: string };

export function VerifyEmail({ onVerified }: { onVerified?: () => void }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>({ kind: "working" });
  // React StrictMode mounts twice in development, which would consume the
  // one-time token on the first mount and report "already used" on the second.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setState({ kind: "failed", reason: "missing" });
      return;
    }

    void fetch(`/api/verify/email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setState({ kind: "ok" });
          onVerified?.();
        } else {
          setState({ kind: "failed", reason: data.error ?? "unknown" });
        }
      })
      .catch(() => setState({ kind: "failed", reason: "network" }));
  }, [token, onVerified]);

  if (state.kind === "working") {
    return <Panel title="Confirming your email address…" tone="neutral" />;
  }

  if (state.kind === "ok") {
    return (
      <Panel title="Email address confirmed" tone="ok">
        <p>Your account is now active.</p>
        <p style={{ marginTop: 16 }}>
          <Link to="/login" style={button}>
            Sign in
          </Link>
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="This link did not work" tone="bad">
      <p>{explain(state.reason)}</p>
      <p style={{ marginTop: 16, fontSize: 14 }}>
        Sign in and request a new confirmation email, or{" "}
        <Link to="/register" style={linkStyle}>
          create an account
        </Link>
        .
      </p>
      <p style={{ marginTop: 12 }}>
        <Link to="/login" style={button}>
          Sign in
        </Link>
      </p>
    </Panel>
  );
}

function explain(reason: string): string {
  switch (reason) {
    case "expired":
      return "The link has expired. Confirmation links are valid for 15 minutes.";
    case "not-found":
      return "This link has already been used, or it is not valid.";
    case "too-many-attempts":
      return "Too many attempts were made with this link. Request a new one.";
    case "missing":
      return "The address is incomplete — no confirmation code was found in it.";
    case "network":
      return "The server could not be reached. Check your connection and try again.";
    default:
      return "Something went wrong confirming your email address.";
  }
}

function Panel({
  title,
  tone,
  children,
}: {
  title: string;
  tone: "ok" | "bad" | "neutral";
  children?: React.ReactNode;
}) {
  const colour = tone === "ok" ? "#2e7d32" : tone === "bad" ? "#c62828" : "#555";
  return (
    <div style={{ maxWidth: 460 }}>
      <h2 style={{ marginTop: 0, color: colour }}>{title}</h2>
      <div style={{ color: "#444", lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

const button: React.CSSProperties = {
  display: "inline-block",
  padding: "9px 18px",
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  background: "#00695c",
  borderRadius: 6,
  textDecoration: "none",
};

const linkStyle: React.CSSProperties = { color: "#00695c", fontWeight: 600 };
